import { Action, Reducer } from 'redux';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonIdentifiers.Actions';
import { PersonIdentifierState, PersonIdentifierUnloadState } from '../states/PersonIdentifierState';
import { PersonIdentifierViewModel } from '../../models/Entities/Person/Identifier/PersonIdentifierModels';

export type KnownAction =
    | Actions.PersonIdentifiers.PersonIdentifierGetAllRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierGetAllSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierGetAllFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierGetByIdRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierGetByIdSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierGetByIdFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierCreateRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierCreateSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierCreateFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierUpdateRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierUpdateSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierUpdateFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierDeleteRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierDeleteSuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierDeleteFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifyRequestAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifySuccessAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifyFailureAction
    | Actions.PersonIdentifiers.PersonIdentifierVerifyResetAction;

export const PersonIdentifiersReducer: Reducer<PersonIdentifierState> = (
    state: PersonIdentifierState | undefined,
    incomingAction: Action
): PersonIdentifierState => {
    if (state === undefined) {
        return PersonIdentifierUnloadState as PersonIdentifierState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.PERSON_IDENTIFIER_GETALL_REQUEST:
            return {
                ...state,
                isLoadingAll: true,
                successLoadingAll: undefined,
                failOnLoadingAll: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_GETALL_SUCCESS: {
            const otherPersons = state.list.filter((item: PersonIdentifierViewModel) => item.personId !== action.personId);
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: true,
                failOnLoadingAll: false,
                list: [...otherPersons, ...action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_GETALL_FAILURE:
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: false,
                failOnLoadingAll: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_GETBYID_REQUEST:
            return {
                ...state,
                isLoadingOne: true,
                successLoadingOne: undefined,
                failOnLoadingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_GETBYID_SUCCESS: {
            const filtered = state.list.filter((item: PersonIdentifierViewModel) => item.id !== action.payload.id);
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: true,
                failOnLoadingOne: false,
                list: [...filtered, action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_GETBYID_FAILURE:
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: false,
                failOnLoadingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_CREATE_REQUEST:
            return {
                ...state,
                isAddingNewOne: true,
                successAddingNewOne: undefined,
                failOnAddingNewOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_CREATE_SUCCESS: {
            const createdList = state.list.some((item: PersonIdentifierViewModel) => item.id === action.payload.id)
                ? state.list.map((item: PersonIdentifierViewModel) => (item.id === action.payload.id ? action.payload : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: true,
                failOnAddingNewOne: false,
                lastCreated: action.payload,
                list: createdList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_CREATE_FAILURE:
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: false,
                failOnAddingNewOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_UPDATE_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_UPDATE_SUCCESS: {
            const updatedList = state.list.some((item: PersonIdentifierViewModel) => item.id === action.payload.id)
                ? state.list.map((item: PersonIdentifierViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: updatedList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_UPDATE_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_DELETE_REQUEST:
            return {
                ...state,
                isDeletingOne: true,
                successDeletingOne: undefined,
                failOnDeletingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_DELETE_SUCCESS:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: true,
                failOnDeletingOne: false,
                list: state.list.filter((item: PersonIdentifierViewModel) => item.id !== action.id),
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_DELETE_FAILURE:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: false,
                failOnDeletingOne: true,
                error: action.error
            };
        case ActionEvents.PERSON_IDENTIFIER_VERIFY_REQUEST:
            return {
                ...state,
                isVerifying: true,
                successVerifying: undefined,
                failOnVerifying: false,
                isIdentifierAvailable: undefined,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_VERIFY_SUCCESS:
            return {
                ...state,
                isVerifying: false,
                successVerifying: true,
                failOnVerifying: false,
                isIdentifierAvailable: action.payload,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_VERIFY_FAILURE:
            return {
                ...state,
                isVerifying: false,
                successVerifying: false,
                failOnVerifying: true,
                isIdentifierAvailable: undefined,
                error: action.error
            };
        case ActionEvents.PERSON_IDENTIFIER_VERIFY_RESET:
            return {
                ...state,
                isVerifying: false,
                successVerifying: undefined,
                failOnVerifying: false,
                isIdentifierAvailable: undefined
            };
        default:
            return state;
    }
};
